'use client';

import { useTranslation } from 'react-i18next';
import { Button, Callout, Spinner } from '@galapagos/design-system/web';
import type { SignupQuizContent } from '@galapagos/shared/registration';
import { usePrefetchBaselineReads } from '../baseline/useBaselineStep';
import { FormActions } from '../components/FormActions';
import { PageTitle } from '../components/PageTitle';
import { SignupShell } from '../components/SignupShell';
import { ensureSignupStepStyle } from '../components/signup-step-style';
import { useCatalogWords } from '../hooks/useCatalogWords';
import { useLegacySignupParams } from '../hooks/useLegacySignupParams';
import { useSignupNavigation } from '../hooks/useSignupNavigation';
import { useSignupQuizContent } from '../hooks/useSignupQuizContent';
import { useStepHeading } from '../hooks/useStepHeading';
import type { CatalogWords } from '../services/catalog-words';
import {
  primaryQuizProgram,
  wordedQuizQuestion,
  type WordedQuizQuestion,
} from '../services/quiz-content';
import { SignupNotImplementedError } from '../services/signup-api/signup-api';
import { useSignupFlowStore } from '../stores/signup-flow-store';
import type { SignupStepId } from '../steps';
import { QuizQuestionScreen } from './QuizQuestionScreen';
import { EarlierAnswersGate } from './EarlierAnswersGate';

type QuizStepId = Extract<SignupStepId, 'quiz-motivation' | 'quiz-goal'>;

/** The content state screens: a title, an optional body, and Continue or Retry. */
export function QuizStateScreen({
  title,
  description,
  busy,
  error,
  action,
}: {
  title: string;
  description?: string;
  busy?: boolean;
  error?: string;
  action?: { kind: 'continue' | 'retry'; onPress: () => void };
}) {
  const { t } = useTranslation('signup');
  const headingRef = useStepHeading(title);
  ensureSignupStepStyle();
  return (
    <SignupShell>
      <div className="signup-step">
        <PageTitle title={title} description={description} headingRef={headingRef} />
        <p className="signup-visually-hidden" aria-live="polite" data-testid="signup-quiz-status">
          {error ?? ''}
        </p>
        {busy ? <Spinner accessibilityLabel={title} /> : null}
        {error ? (
          <div aria-hidden="true">
            <Callout tone="error" body={error} />
          </div>
        ) : null}
        {action ? (
          <FormActions>
            {action.kind === 'continue' ? (
              <Button
                size="L"
                type="Primary"
                label={t('actions.continue')}
                onPress={action.onPress}
                actionId="signup.quiz-skip.activate"
                data-testid="signup-continue"
              />
            ) : (
              <Button
                size="L"
                type="Primary"
                label={t('quiz.retry')}
                onPress={action.onPress}
                actionId="signup.quiz-retry.activate"
                data-testid="signup-quiz-retry"
              />
            )}
          </FormActions>
        ) : null}
      </div>
    </SignupShell>
  );
}

function questionFor(
  words: CatalogWords,
  step: QuizStepId,
  content: SignupQuizContent,
  selection: readonly string[],
): WordedQuizQuestion {
  return step === 'quiz-goal'
    ? wordedQuizQuestion(words, content.goal, primaryQuizProgram(content.goal, selection))
    : wordedQuizQuestion(words, content.motivation);
}

function QuizStep({ step }: { step: QuizStepId }) {
  // A reload on this step refills the flow's params from its URL.
  useLegacySignupParams();
  const content = useSignupQuizContent();
  const selection = useSignupFlowStore((state) => state.programSelection);
  const { goToNextStep } = useSignupNavigation();
  const { t } = useTranslation('signup');
  const words = useCatalogWords();
  // The last-week step follows the goal question: start its reads now so its
  // table is ready when the member arrives.
  usePrefetchBaselineReads(step === 'quiz-goal' && content.isSuccess);

  // A retry after a failure refetches while the query still holds its error,
  // so the step shows loading again rather than the stale error.
  if (content.isPending || (content.isError && content.isFetching)) {
    return <QuizStateScreen title={t('quiz.loading')} busy />;
  }
  if (content.isError) {
    // No backend serves the content yet on deployed builds: stand in for the
    // step, like an unbuilt segment, so the path still clicks through.
    if (content.error instanceof SignupNotImplementedError) {
      return (
        <QuizStateScreen
          title={t('placeholder.title')}
          description={t('placeholder.description')}
          action={{ kind: 'continue', onPress: () => goToNextStep(step) }}
        />
      );
    }
    return (
      <QuizStateScreen
        title={t('quiz.loadErrorTitle')}
        error={t('quiz.loadError')}
        action={{ kind: 'retry', onPress: () => void content.refetch() }}
      />
    );
  }
  return (
    <QuizQuestionScreen
      key={step}
      question={questionFor(words, step, content.data, selection ?? [])}
      onAnswered={() => goToNextStep(step)}
    />
  );
}

/** `/quiz-motivation/`: what's motivating the member right now (v9 1.9). */
export function QuizMotivationScreen() {
  return (
    <EarlierAnswersGate step="quiz-motivation">
      <QuizStep step="quiz-motivation" />
    </EarlierAnswersGate>
  );
}

/** `/quiz-goal/`: what the member wants right now (v9 1.10). */
export function QuizGoalScreen() {
  return (
    <EarlierAnswersGate step="quiz-goal">
      <QuizStep step="quiz-goal" />
    </EarlierAnswersGate>
  );
}
